import PropTypes from "prop-types";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

import PageTitleBar from "@/components/PageTitleBar";
import { Spinner } from "@/components/ui/spinner";
import ACL_RELATIONSHIPS from "acl-relationships";
import useAuthorized from "hooks/useAuthorized";
import { useEmailUrl } from "hooks/useEmailUrlsTable";
import usePageTitle from "hooks/usePageTitle";
import EmailUrlForm from "routes/email_urls/components/EmailUrlForm";
import { EMAIL_URLS_PATH } from "routes/email_urls/components/email_urls_table/EmailUrlsTableV3";

const BACK_LABEL = "Back to Email URLs";

const EmailUrl = ({ mode }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = mode === "edit";

  const canWrite = useAuthorized([
    isEdit
      ? ACL_RELATIONSHIPS.emailUrl.edit
      : ACL_RELATIONSHIPS.emailUrl.create,
  ]);

  const {
    data: emailUrl,
    isSuccess,
    isError,
    isFetchedAfterMount,
  } = useEmailUrl(isEdit ? id : null);

  const pageTitle = isEdit ? "Edit Email URL" : "New Email URL";
  usePageTitle(isEdit ? emailUrl?.name : pageTitle);

  const returnToList = () => navigate(EMAIL_URLS_PATH);

  const handleSaved = () => {
    toast.success(isEdit ? "Email URL updated" : "Email URL created");
    returnToList();
  };

  if (!canWrite) {
    return <Navigate to={EMAIL_URLS_PATH} replace />;
  }

  if (isError) {
    return (
      <div className="tw:w-full tw:p-8 tw:text-center tw:text-(--color-text-muted)">
        <p>{"Couldn't load this Email URL. Please try again."}</p>
        <Link to={EMAIL_URLS_PATH}>{BACK_LABEL}</Link>
      </div>
    );
  }

  // isFetchedAfterMount, not just isSuccess: a cached entry resolves
  // synchronously as successful, and react-hook-form latches defaultValues at
  // the form's first render. Mounting on that payload would strand the form on
  // pre-save values, and saving would write them back over a newer edit.
  if (isEdit && !(isSuccess && isFetchedAfterMount)) {
    return (
      <div className="tw:w-full tw:p-8 tw:text-center tw:text-(--color-text-muted)">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="tw:w-full tw:self-start tw:flex tw:justify-center tw:font-sans tw:text-(--color-text-primary)">
      <div className="tw:flex tw:w-full tw:max-w-5xl tw:flex-col tw:gap-4">
        <PageTitleBar
          backTo={EMAIL_URLS_PATH}
          backLabel={BACK_LABEL}
          title={pageTitle}
          subtitle={emailUrl?.name}
        />
        <EmailUrlForm
          emailUrl={emailUrl}
          onSaved={handleSaved}
          onCancel={returnToList}
        />
      </div>
    </div>
  );
};

EmailUrl.propTypes = {
  mode: PropTypes.oneOf(["create", "edit"]).isRequired,
};

export default EmailUrl;
